import React, { useCallback, useEffect, useState } from 'react';
import './ImageUpload.css';

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_FILE_SIZE = 15 * 1024 * 1024;

export interface SelectedImage {
  dataUrl: string;
  /** Original file name, used as the default name in the editor's save dialog. */
  fileName: string;
}

interface ImageUploadProps {
  onImageSelected: (image: SelectedImage) => void;
}

const ImageUpload = ({ onImageSelected }: ImageUploadProps) => {
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const readFile = useCallback(
    (file: File) => {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        setError('Please choose a PNG, JPG or WEBP image.');
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        setError('That image is larger than 15 MB. Please choose a smaller one.');
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        setError(null);
        onImageSelected({ dataUrl: reader.result as string, fileName: file.name });
      };
      reader.onerror = () => setError('We could not read that file. Please try another image.');
      reader.readAsDataURL(file);
    },
    [onImageSelected]
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset the input so picking the same file twice still fires a change event.
    event.target.value = '';

    if (file) {
      readFile(file);
    }
  };

  // Drop an image anywhere on the page.
  useEffect(() => {
    // dragenter/dragleave fire for every child element, so count depth rather
    // than clearing the highlight on the first leave.
    let depth = 0;

    const onDragEnter = (event: DragEvent) => {
      event.preventDefault();
      depth += 1;
      setIsDragging(true);
    };
    const onDragOver = (event: DragEvent) => event.preventDefault();
    const onDragLeave = () => {
      depth = Math.max(0, depth - 1);
      if (depth === 0) {
        setIsDragging(false);
      }
    };
    const onDrop = (event: DragEvent) => {
      event.preventDefault();
      depth = 0;
      setIsDragging(false);
      const file = event.dataTransfer?.files?.[0];
      if (file) {
        readFile(file);
      }
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);

    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [readFile]);

  // Paste an image from the clipboard.
  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const file = event.clipboardData?.files?.[0];
      if (file) {
        event.preventDefault();
        readFile(file);
      }
    };

    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [readFile]);

  return (
    <div className={`start-panel${isDragging ? ' is-dragging' : ''}`}>
      <p className="eyebrow start-panel-label">Start here</p>

      <div className="start-panel-dropzone" aria-hidden="true">
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.8" cy="9" r="1.6" />
          <path d="m3.6 16.8 4.3-4.3a2 2 0 0 1 2.8 0l4.5 4.5" />
          <path d="m14.2 14.6 1.6-1.6a2 2 0 0 1 2.8 0l1.8 1.8" />
        </svg>
        <span className="start-panel-dropzone-text">
          {isDragging ? 'Drop to open it' : 'Drop an image here'}
        </span>
      </div>

      <input
        className="visually-hidden"
        id="image-upload-input"
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        onChange={handleChange}
      />
      <label className="start-panel-button" htmlFor="image-upload-input">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="6" width="18" height="14" rx="2.5" />
          <path d="M9 6 10.5 3h3L15 6" />
          <circle cx="12" cy="13" r="3.6" />
        </svg>
        <span className="start-panel-button-wide">Choose a file</span>
        <span className="start-panel-button-narrow">Choose from library</span>
      </label>

      <input
        className="visually-hidden"
        id="image-capture-input"
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleChange}
      />
      <label className="start-panel-button is-secondary" htmlFor="image-capture-input">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M14.5 4h-5L8 6.5H4.5A1.5 1.5 0 0 0 3 8v10a1.5 1.5 0 0 0 1.5 1.5h15A1.5 1.5 0 0 0 21 18V8a1.5 1.5 0 0 0-1.5-1.5H16Z" />
          <circle cx="12" cy="12.8" r="3.4" />
        </svg>
        Take a photo
      </label>

      <p className="start-panel-hint">PNG, JPG or WEBP &middot; up to 15&#8239;MB</p>

      {error && (
        <p className="start-panel-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default ImageUpload;
